import * as React from "react";
import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { ConfirmModalAction } from "../../components/common/confirm-modal";
import { apirequest } from "../../helpers/api-client";
import { t } from "../../constant/translations";
import {
  Button,
  Checkbox,
  Grid,
  Header,
  Message,
  Table,
} from "semantic-ui-react";
import { Currency } from "../../containers/common/currency";
import { BGN, EUR } from "../../helpers/currency";

export const PayByCardAction = ({ estate, disabled, prepayment }) => {
  const [loading, setLoading] = useState(false);
  const [billingForecast, setBillingForecast] = useState(null);
  const [prepayEnabled, setPrepayEnabled] = useState(false);
  const [additionalAmount, setAdditionalAmount] = useState(0);
  const [selectedMonths, setSelectedMonths] = useState(undefined);

  useEffect(() => {
    fetchBillingForecast();
    if (prepayment) {
      setPrepayEnabled(true);
    }
  }, [prepayment]);

  const fetchBillingForecast = async () => {
    try {
      const result = await apirequest(
        "get",
        `/api/billing-forecast/${estate.id}/`
      );
      setBillingForecast(result.body);
    } catch (error) {
      console.error("Failed to fetch billing forecast:", error);
    }
  };

  const handlePrepaySelect = (months) => {
    if (months === selectedMonths) {
      setSelectedMonths(undefined);
      setAdditionalAmount(0);
    } else if (billingForecast) {
      const newAdditionalAmount = billingForecast.total * months;
      setAdditionalAmount(newAdditionalAmount);
      setSelectedMonths(months);
    }
  };

  const onAction = async (event) => {
    event.preventDefault();
    setLoading(true);

    const payload = {
      ...estate,
      additional_amount: additionalAmount,
    };

    const result = await apirequest("post", `/api/checkout/init/`, payload);
    if (result.body.url) {
      window.location.replace(result.body.url);
    }
  };

  const calculateFee = (amount) => {
    return amount ? Math.max(amount * 0.02, 1.02) : 0;
  };

  const calculateGrandTotal = () => {
    const subtotal = parseFloat(estate.debt) + additionalAmount;
    const fee = calculateFee(subtotal);
    return subtotal + fee;
  };

  const grandTotal = calculateGrandTotal();
  const content = (
    <>
      <Grid columns={1} divided>
        <Grid.Row>
          <Grid.Column>
            <Header as="h4">{t("estate.payment_details")}</Header>
            <Table celled>
              <Table.Body>
                <Table.Row>
                  <Table.Cell>{t("estate.current_debt")}</Table.Cell>
                  <Table.Cell width={4}>
                    <Currency amount={estate.debt} />
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>{t("estate.prepay_amount")}</Table.Cell>
                  <Table.Cell width={4}>
                    <Currency amount={additionalAmount} />
                  </Table.Cell>
                </Table.Row>
                <Table.Row>
                  <Table.Cell>{t("estate.transaction_fee")}</Table.Cell>
                  <Table.Cell width={4}>
                    <Currency
                      amount={calculateFee(
                        parseFloat(estate.debt) + (additionalAmount || 0)
                      )}
                    />
                  </Table.Cell>
                </Table.Row>
                <Table.Row positive>
                  <Table.Cell>
                    <strong>{t("estate.total_amount")}</strong>
                  </Table.Cell>
                  <Table.Cell width={4}>
                    <strong>
                      <Currency amount={grandTotal} selectedCurrency={BGN} />
                      {" / "}
                      <Currency amount={grandTotal} selectedCurrency={EUR} />
                    </strong>
                  </Table.Cell>
                </Table.Row>
              </Table.Body>
            </Table>
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column width={16}>
            <Checkbox
              toggle
              label={t("estate.enable_prepay")}
              checked={prepayEnabled}
              onChange={(e, data) => {
                setPrepayEnabled(data.checked);
                setAdditionalAmount(0);
                setSelectedMonths(0);
              }}
            />
            {prepayEnabled && (
              <>
                <Header as="h4">{t("estate.choose_prepay_period")}</Header>
                {t("estate.typical_monthly_payment")}:&nbsp;
                {billingForecast ? (
                  <strong>
                    <Currency amount={billingForecast.total} />
                  </strong>
                ) : (
                  <em>{t("estate.loading_forecast")}</em>
                )}
                <Button.Group fluid style={{ marginTop: "10px" }}>
                  {[1, 3, 6, 12].map((months) => (
                    <Button
                      key={months}
                      toggle={true}
                      onClick={() => handlePrepaySelect(months)}
                      color="grey"
                      active={selectedMonths === months}
                    >
                      {t(`estate.prepay_${months}_months`)}
                    </Button>
                  ))}
                </Button.Group>
              </>
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>
      <Message
        warning
        icon="warning"
        content={t("component.pay-by-card.transaction_fee_info")}
      />
    </>
  );

  return (
    <ConfirmModalAction
      header={`${t("estate.payment_to")} ${estate.name}`}
      content={content}
      trigger_props={{
        size: "tiny",
        positive: true,
        content: prepayment ? t("estate.prepay") : t("estate.pay"),
        disabled: disabled,
      }}
      error={null}
      loading={loading}
      action={onAction}
      confirmLabel={
        <>
          {t("estate.pay")}{" "}
          <Currency amount={grandTotal} selectedCurrency={BGN} /> /{" "}
          <Currency amount={grandTotal} selectedCurrency={EUR} />
        </>
      }
      confirmDisabled={grandTotal <= 0}
    />
  );
};

PayByCardAction.propTypes = {
  estate: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  prepayment: PropTypes.bool,
};
